import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Two-way lazy-render hook using IntersectionObserver.
 *
 * - Returns `true` when the element is within `showMargin` of the viewport
 *   → triggers chart mount / ECharts init.
 * - Returns `false` when the element scrolls beyond `hideMargin`
 *   → triggers chart unmount, freeing DOM listeners and memory.
 *
 * The generous `hideMargin` prevents rapid mount/unmount during normal
 * scrolling — a chart only unmounts when it's far off-screen.
 *
 * @param showMargin  Distance to start rendering (default `"200px"`).
 * @param hideMargin  Distance to unmount (default `"1000px"`).
 *                    Must be >= showMargin to avoid flicker.
 */
export function useVisibility<T extends HTMLElement = HTMLDivElement>(
  showMargin = '200px',
  hideMargin = '1000px',
): { ref: RefObject<T | null>; isVisible: boolean } {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Choose margin based on current state:
    // - Not visible → use tight showMargin to detect entry
    // - Visible → use generous hideMargin to detect exit
    const margin = isVisible ? hideMargin : showMargin;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { rootMargin: margin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [showMargin, hideMargin, isVisible]);

  return { ref, isVisible };
}